import { GzDashboardSaveAsDialogContentWebComponent } from '../gzDashboardPopUps/gzDashboardSaveAsDialogContent';
import { RequiredValidator } from '../../ui/gzForm/validators/required.validator';
import { GzDialogWebComponent } from '../../ui/gzDialog';
import { translateFn } from '../../utils/translator.helper';
import { GzDialogEvents } from '../../ui/gzDialog/component/GzDialog';
import { GzDashboardDeleteViewDialogContentWebComponent } from '../gzDashboardPopUps/gzDashboardDeleteViewDialogContent';
import { GzDashboardDiscardChangesDialogContentWebComponent } from '../gzDashboardPopUps/gzDashboardDiscardChangesDialogContent';
import { GzDashboardUnsavedChangesDialogContentWebComponent } from '../gzDashboardPopUps/gzDashboardUnsavedChangesDialogContent';
import { FormValidator } from '../../ui/gzForm/formField/types/form-validator';
import { GzDashboardFeedBackDialogContentWebComponent } from '../gzDashboardPopUps/gzDashboardFeedBackDialogContent';
import { FeedbackType } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';

export enum DashboardPopUpType {
  SAVE_AS_DIALOG = 1,
  DELETE_VIEW_DIALOG = 2,
  DISCARD_CHANGES_DIALOG = 3,
  UNSAVED_CHANGES_DIALOG = 4,
  FEEDBACK_VIEW_DIALOG = 5,
  FEEDBACK_VIEW_DIALOG_COMPLEX = 6,
}

export interface PopUpConfiguration {
  validators?: Array<FormValidator>;
  viewName?: string;
  hasExtraInfo?: boolean;
  parentElement?: HTMLElement | null | undefined;
  feedbackType?: FeedbackType;
}

export class DashboardPopUpFactory {
  public static createPopUp(
    popUpType: DashboardPopUpType,
    confirmCallbackFn: Function = () => {},
    cancelCallbackFn: Function = () => {},
    configuration: PopUpConfiguration = {}
  ): void {
    switch (popUpType) {
      case DashboardPopUpType.SAVE_AS_DIALOG:
        DashboardPopUpFactory.displaySaveAsDialog(confirmCallbackFn, cancelCallbackFn, configuration);
        break;
      case DashboardPopUpType.DELETE_VIEW_DIALOG:
        DashboardPopUpFactory.displayDeleteViewDialog(confirmCallbackFn, cancelCallbackFn, configuration);
        break;
      case DashboardPopUpType.DISCARD_CHANGES_DIALOG:
        DashboardPopUpFactory.displayDiscardChangesDialog(confirmCallbackFn, cancelCallbackFn, configuration);
        break;
      case DashboardPopUpType.UNSAVED_CHANGES_DIALOG:
        DashboardPopUpFactory.displayUnsavedChangesDialog(confirmCallbackFn, cancelCallbackFn, configuration);
        break;
      case DashboardPopUpType.FEEDBACK_VIEW_DIALOG:
        DashboardPopUpFactory.displayFeedBackDialog(confirmCallbackFn, cancelCallbackFn, configuration);
        break;
      case DashboardPopUpType.FEEDBACK_VIEW_DIALOG_COMPLEX:
        DashboardPopUpFactory.displayComplexFeedBackDialog(confirmCallbackFn, cancelCallbackFn, configuration);
        break;
      default:
        throw new Error(`Unknown dashboard pop-up type:${popUpType}`);
    }
  }

  private static displaySaveAsDialog(
    confirmCallbackFn: Function,
    cancelCallbackFn: Function,
    configuration: PopUpConfiguration
  ) {
    const saveAsDialogContent = new GzDashboardSaveAsDialogContentWebComponent();
    saveAsDialogContent.validators = [RequiredValidator(translateFn('This field is required.'))];
    if (configuration.validators) {
      saveAsDialogContent.validators.push(...configuration.validators);
    }

    const dialog = GzDialogWebComponent.buildDialogElement({
      headline: translateFn('Save view?'),
      bodyContent: saveAsDialogContent,
      isOpen: true,
    });

    saveAsDialogContent.addEventListener(GzDialogEvents.CANCEL, (): void => {
      cancelCallbackFn();
      dialog.close();
    });
    saveAsDialogContent.addEventListener(GzDialogEvents.CONFIRM, (event: CustomEvent): void => {
      confirmCallbackFn(event);
      dialog.close();
    });
  }

  private static displayDeleteViewDialog(
    confirmCallbackFn: Function,
    cancelCallbackFn: Function,
    configuration: PopUpConfiguration
  ) {
    if (configuration.viewName === undefined) {
      throw new Error('Cannot delete view without name');
    }
    const deleteViewDialogContent = new GzDashboardDeleteViewDialogContentWebComponent();
    deleteViewDialogContent.viewName = configuration.viewName;

    const dialog = GzDialogWebComponent.buildDialogElement({
      headline: translateFn('Delete view?'),
      bodyContent: deleteViewDialogContent,
      isOpen: true,
    });

    deleteViewDialogContent.addEventListener(GzDialogEvents.CANCEL, (): void => {
      cancelCallbackFn();
      dialog.close();
    });
    deleteViewDialogContent.addEventListener(GzDialogEvents.CONFIRM, (event: CustomEvent): void => {
      confirmCallbackFn(event);
      dialog.close();
    });
  }

  private static displayDiscardChangesDialog(
    confirmCallbackFn: Function,
    cancelCallbackFn: Function,
    configuration: PopUpConfiguration
  ) {
    const discardChangesDialogContent = new GzDashboardDiscardChangesDialogContentWebComponent();

    const dialog = GzDialogWebComponent.buildDialogElement({
      headline: translateFn('Discard changes?'),
      bodyContent: discardChangesDialogContent,
      isOpen: true,
    });

    discardChangesDialogContent.addEventListener(GzDialogEvents.CANCEL, (): void => {
      cancelCallbackFn();
      dialog.close();
    });
    discardChangesDialogContent.addEventListener(GzDialogEvents.CONFIRM, (event: CustomEvent): void => {
      confirmCallbackFn(event);
      dialog.close();
    });
  }

  private static displayUnsavedChangesDialog(
    confirmCallbackFn: Function,
    cancelCallbackFn: Function,
    configuration: PopUpConfiguration
  ) {
    const dashboardUnsavedChangesDialogContent = new GzDashboardUnsavedChangesDialogContentWebComponent();

    const dialog = GzDialogWebComponent.buildDialogElement({
      headline: translateFn('Leaving edit mode?'),
      bodyContent: dashboardUnsavedChangesDialogContent,
      isOpen: true,
    });

    dashboardUnsavedChangesDialogContent.addEventListener(GzDialogEvents.CANCEL, (): void => {
      cancelCallbackFn();
      dialog.close();
    });
    dashboardUnsavedChangesDialogContent.addEventListener(GzDialogEvents.CONFIRM, (event: CustomEvent): void => {
      confirmCallbackFn(event);
      dialog.close();
    });
  }

  private static displayFeedBackDialog(
    confirmCallbackFn: Function,
    cancelCallbackFn: Function,
    configuration: PopUpConfiguration
  ): void {
    const feedbackDialogContent = new GzDashboardFeedBackDialogContentWebComponent();
    if (configuration.validators) {
      feedbackDialogContent.validators.push(...configuration.validators);
    }
    if (configuration.hasExtraInfo) {
      feedbackDialogContent.hasExtraInfo = configuration.hasExtraInfo;
    }

    const dialog = GzDialogWebComponent.buildDialogElement(
      {
        headline: translateFn('Submit feedback'),
        bodyContent: feedbackDialogContent,
        isOpen: true,
        isWindow: false,
        overlayZIndex: 99999
      },
      configuration.parentElement ? configuration.parentElement : undefined
    );

    feedbackDialogContent.addEventListener(GzDialogEvents.CANCEL, (): void => {
      cancelCallbackFn();
      dialog.close();
    });
    feedbackDialogContent.addEventListener(GzDialogEvents.CONFIRM, (event: CustomEvent): void => {
      confirmCallbackFn(event);
      dialog.close();
    });
  }

  private static displayComplexFeedBackDialog(
    confirmCallbackFn: Function,
    cancelCallbackFn: Function,
    configuration: PopUpConfiguration
  ): void {
    const feedbackDialogContent = new GzDashboardFeedBackDialogContentWebComponent();
    if (configuration.validators) {
      feedbackDialogContent.validators.push(...configuration.validators);
    }
    if (configuration.hasExtraInfo) {
      feedbackDialogContent.hasExtraInfo = configuration.hasExtraInfo;
    }
    feedbackDialogContent.complexPopup = true;
    feedbackDialogContent.feedbackType = configuration.feedbackType ?? FeedbackType.HEALTH_DASHBOARD;

    const dialog = GzDialogWebComponent.buildDialogElement(
      {
        headline: translateFn('Submit feedback'),
        bodyContent: feedbackDialogContent,
        isOpen: true,
        isWindow: true,
      },
      configuration.parentElement ? configuration.parentElement : undefined
    );

    feedbackDialogContent.addEventListener(GzDialogEvents.CANCEL, (): void => {
      cancelCallbackFn();
      dialog.close();
    });
    feedbackDialogContent.addEventListener(GzDialogEvents.CONFIRM, (event: CustomEvent): void => {
      confirmCallbackFn(event);
      dialog.close();
    });
  }
}
